import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, FileUp, Database } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["kiotviet", "misa", "mobiwork", "excel_custom"];
const FIELDS = [
  ["emp_code", "Mã NV"], ["emp_name", "Họ tên"], ["period", "Kỳ (YYYY-MM)"],
  ["base_salary", "Lương cơ bản"], ["bonus", "Thưởng"], ["deductions", "Khấu trừ"], ["net_pay", "Thực lĩnh"],
];

export default function LegacyPayroll() {
  const [workspaces, setWorkspaces] = useState([]);
  const [active, setActive] = useState(null);
  const [legacy, setLegacy] = useState([]);
  const [summary, setSummary] = useState([]);
  const [mapping, setMapping] = useState({});
  const [source, setSource] = useState("kiotviet");

  useEffect(() => {
    api.get("/excel/workspaces").then((r) => setWorkspaces(r.data));
    api.get("/payroll/legacy/summary").then((r) => setSummary(r.data));
    api.get("/payroll/legacy").then((r) => setLegacy(r.data));
  }, []);

  const loadWorkspace = async (id) => {
    const r = await api.get(`/excel/workspaces/${id}`);
    setActive(r.data); setMapping({});
  };

  const doImport = async () => {
    if (!active) { toast.error("Chọn workspace"); return; }
    try {
      const r = await api.post("/payroll/import-legacy", {
        workspace_id: active.id, source, mapping,
      });
      toast.success(`✓ Import ${r.data.inserted} dòng từ ${source}`);
      api.get("/payroll/legacy/summary").then((r) => setSummary(r.data));
      api.get("/payroll/legacy").then((r) => setLegacy(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  return (
    <div data-testid="page-legacy">
      <PageHeader
        kicker="Migration • Excel"
        title="Import lương cũ từ KiotViet / Misa"
        breadcrumb={["Công cụ", "Import lương legacy"]}
      />
      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm">
            <strong>Quy trình</strong>: 1) Upload Excel KiotViet/Misa vào <em>Excel Workspace</em> trước → 2) Quay lại đây chọn workspace → 3) Map cột Excel → 4) Bấm Import.
          </div>

          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
              <FileUp className="w-4 h-4 text-emerald-800" />
              <h3 className="font-serif text-lg font-bold">Cấu hình Import</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nguồn dữ liệu</Label>
                  <Select value={source} onValueChange={setSource}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Workspace Excel</Label>
                  <Select value={active?.id || ""} onValueChange={loadWorkspace}>
                    <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn file" /></SelectTrigger>
                    <SelectContent>{workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({w.n_rows} dòng)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {active && (
                <div>
                  <Label className="text-xs">Map cột Excel → Trường lương</Label>
                  <div className="border border-stone-200 rounded-sm divide-y divide-stone-100 mt-1">
                    {FIELDS.map(([k, lbl]) => (
                      <div key={k} className="px-3 py-2 flex items-center gap-2">
                        <div className="w-1/3 text-xs font-semibold">{lbl}</div>
                        <Select value={mapping[k] || undefined} onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v }))}>
                          <SelectTrigger className="h-8 rounded-sm flex-1"><SelectValue placeholder="(bỏ qua)" /></SelectTrigger>
                          <SelectContent>{active.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <Button onClick={doImport} className="w-full mt-3 bg-emerald-800 hover:bg-emerald-900 rounded-sm h-11" data-testid="btn-do-import">
                    <Database className="w-4 h-4 mr-1.5" />Import {active.rows.length} dòng vào hệ thống
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Recent imported */}
          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
              <History className="w-4 h-4 text-stone-500" />
              <h3 className="font-serif text-base font-bold">Dữ liệu đã import (gần nhất)</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b">
                <tr>{["Nguồn","Kỳ","Mã NV","Họ tên","Lương CB","Thưởng","Khấu trừ","Thực lĩnh"].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {legacy.slice(0, 30).map((l) => (
                  <tr key={l.id} className="hover:bg-stone-50" data-testid={`legacy-row-${l.id}`}>
                    <td className="px-3 py-1.5"><span className="text-[10px] px-1 py-0.5 bg-stone-200 rounded-sm uppercase">{l.source}</span></td>
                    <td className="px-3 py-1.5 font-mono text-xs">{l.period}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{l.emp_code}</td>
                    <td className="px-3 py-1.5 font-semibold">{l.emp_name}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-right">{fmtMoney(l.base_salary)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-right text-emerald-800">{fmtMoney(l.bonus)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-right text-red-700">{fmtMoney(l.deductions)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-right font-bold">{fmtMoney(l.net_pay)}</td>
                  </tr>
                ))}
                {legacy.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-stone-400">Chưa có dữ liệu legacy</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar: summary */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white border border-stone-200 rounded-sm">
            <div className="px-4 py-3 border-b">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Roll-up</div>
              <h3 className="font-serif text-lg font-bold">Đã import theo nguồn × kỳ</h3>
            </div>
            <ul className="divide-y divide-stone-100">
              {summary.map((s, i) => (
                <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <div className="text-xs"><span className="text-[10px] px-1 py-0.5 bg-stone-200 rounded-sm uppercase">{s.source}</span> <span className="font-mono">{s.period}</span></div>
                    <div className="text-[10px] text-stone-500 mt-0.5">{s.n} bản ghi</div>
                  </div>
                  <div className="font-mono text-sm font-bold text-emerald-800">{fmtMoney(s.total_net)}</div>
                </li>
              ))}
              {summary.length === 0 && <li className="px-4 py-6 text-center text-xs text-stone-400">Chưa có roll-up</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
